import React, { useCallback, useEffect, useState } from 'react';
import { SchoolMfaApi } from 'api/schoolMfaApi';
import { formatShortDate } from 'helpers/formatShortDate';
import { Spinner } from 'components/spinner/Spinner';
import { isValidIpAddress } from 'helpers/mfa/ipAddress';
import { IpWhitelistEntry } from 'pages/mfa/models';
import { RemoveNetworkConfirm } from './RemoveNetworkConfirm';

const MAX_IP_LENGTH = 128;

export const IpWhitelistPanel: React.FC = () => {
  const [entries, setEntries] = useState<IpWhitelistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [newIp, setNewIp] = useState('');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [confirming, setConfirming] = useState<IpWhitelistEntry | null>(null);

  const loadEntries = useCallback(async () => {
    const result = await SchoolMfaApi.getIpWhitelist();
    if (result.error) {
      setLoadError(result.error.message ?? '');
    } else {
      setEntries(result.data ?? []);
      setLoadError('');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const addEntry = async (e: React.FormEvent) => {
    e.preventDefault();
    const ipAddress = newIp.trim();
    if (
      !ipAddress ||
      ipAddress.length > MAX_IP_LENGTH ||
      !isValidIpAddress(ipAddress)
    ) {
      setFormError('Please enter a valid IP address.');
      return;
    }

    setSubmitting(true);
    setFormError('');
    const result = await SchoolMfaApi.createIpWhitelistEntry(ipAddress);
    setSubmitting(false);

    if (result.error) {
      setFormError(result.error.message ?? '');
      return;
    }
    setNewIp('');
    loadEntries();
  };

  const removeEntry = async (entry: IpWhitelistEntry) => {
    setDeletingId(entry.schoolAccountsIpAddressWhitelistId);
    setFormError('');
    const result = await SchoolMfaApi.deleteIpWhitelistEntry(
      entry.schoolAccountsIpAddressWhitelistId
    );
    setDeletingId(null);
    setConfirming(null);

    if (result.error) {
      setFormError(result.error.message ?? '');
      return;
    }
    loadEntries();
  };

  return (
    <div className="IpWhitelistPanel">
      <h2>Recognised school networks</h2>
      <p>
        You can add your school's IP address so staff using trusted devices on
        the school network can go up to 60 days without verifying again, instead
        of 30. Your school's IT team can help with this if needed.
      </p>

      {loading && <Spinner />}
      {!loading && loadError && <p className="MfaError">{loadError}</p>}

      {!loading && !loadError && (
        <div className="IpWhitelist">
          {entries.length === 0 && (
            <p className="EmptyNote">No networks added yet.</p>
          )}
          {entries.map((entry) => (
            <div
              className="IpRow"
              key={entry.schoolAccountsIpAddressWhitelistId}
            >
              <span className="IpAddress">{entry.ipAddress}</span>
              <span className="AddedDate">
                Added {formatShortDate(entry.addDate)}
              </span>
              <button
                type="button"
                className="Btn BtnWhiteGrey"
                onClick={() => setConfirming(entry)}
                disabled={deletingId !== null}
              >
                {deletingId === entry.schoolAccountsIpAddressWhitelistId
                  ? 'Removing'
                  : 'Remove'}
              </button>
            </div>
          ))}
        </div>
      )}

      <form className="AddIpForm" onSubmit={addEntry}>
        <div className="FormBox">
          <div className="FormField">
            <input
              type="text"
              className="EditBox"
              placeholder="Enter an IP address"
              value={newIp}
              maxLength={MAX_IP_LENGTH}
              onChange={(e) => {
                setNewIp(e.target.value);
                setFormError('');
              }}
              disabled={submitting}
              aria-label="IP address"
            />
          </div>
        </div>
        <button
          type="submit"
          className="Btn BtnOrange"
          disabled={submitting || !newIp.trim()}
        >
          {submitting ? 'Adding' : 'Add network'}
        </button>
      </form>
      {formError && <p className="MfaError">{formError}</p>}

      {confirming && (
        <RemoveNetworkConfirm
          ipAddress={confirming.ipAddress}
          isSubmitting={deletingId !== null}
          onConfirm={() => removeEntry(confirming)}
          onCancel={() => deletingId === null && setConfirming(null)}
        />
      )}
    </div>
  );
};

export default IpWhitelistPanel;
